/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VirtualServiceModule
 */

import {
    AfterViewInit,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    Type,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { PoolGroup } from 'items/pool-group.item.factory';
import { DialogService } from 'dialog-service';
import { StringService } from 'string-service';

import {
    AviPermissionResource,
    IService,
    VHType,
    VirtualServiceType,
    WafMode,
} from 'generated-types';

import type {
    VirtualServiceItem,
} from 'items/virtual-service.item.factory';

import { AviAlertService } from 'ng/modules/core/services/avi-alert.service';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { DeleteVsDialogComponent } from 'ng/modules/virtual-service/components/delete-vs-dialog';

import {
    IAviMetricsCollectionDataGridConfig,
} from 'ng/modules/data-grid/components/avi-metrics-collection-data-grid/avi-metrics-collection-data-grid.types';

import {
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';
import { AviDataGridFieldVisibility } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import {
    TVirtualServiceCollection,
    VirtualServiceCollection,
} from 'ng/modules/collections/factories/virtual-service.collection.factory';

import {
    TWafPolicyCollection,
    WafPolicyCollection,
} from 'ajs/modules/waf/factories/waf-policy/waf-policy.collection.factory';

import { StateService } from '@uirouter/core';
import * as globalL10n from 'global-l10n';

import {
    POOL_DETAIL_ANALYTICS,
    SERVICE_ENGINE_DETAIL_SUMMARY,
    VS_DETAIL_ALERTS,
    VS_DETAIL_ANALYTICS,
    VS_DETAIL_HS_INSIGHTS,
} from 'state-names';

import { isUndefined } from 'underscore';

import * as l10n from './vs-list-page.l10n';
import './vs-list-page.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const DELETE_VS_DIALOG_ID = 'delete-vs-dialog';

/**
 * @description Component for Virtual Service Grid.
 * @author Dnyaneshwar Jadhav, Shubam Bindal
 */
@Component({
    selector: 'vs-list-page',
    templateUrl: './vs-list-page.component.html',
})
export class VsListPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('vsNameGridCellTemplate')
    public readonly vsNameGridCellTemplate: TemplateRef<HTMLElement>;

    @ViewChild('healthTemplateRef')
    public readonly healthTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('vsFqdnGridCellTemplate')
    public readonly vsFqdnGridCellTemplate: TemplateRef<HTMLElement>;

    @ViewChild('vsAddressGridCellTemplate')
    public readonly vsAddressGridCellTemplate: TemplateRef<HTMLElement>;

    @ViewChild('poolTemplateRef')
    public readonly poolTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('poolGroupTemplateRef')
    public readonly poolGroupTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('vsServiceEngineTemplateRef')
    public readonly vsServiceEngineTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('virtualHostingTemplateRef')
    public readonly virtualHostingTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('alertTemplateRef')
    public readonly alertTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('virtualHostingRef')
    public readonly virtualHostingRef: TemplateRef<HTMLElement>;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public vsGridConfig: IAviMetricsCollectionDataGridConfig;

    public readonly vsCollection: VirtualServiceCollection;

    private readonly wafEvaluationModePolicies: Set<string> = new Set();

    private readonly wafPolicyCollection: WafPolicyCollection;

    constructor(
        public readonly authService: Auth,
        private readonly l10nService: L10nService,
        private readonly $state: StateService,
        private readonly stringService: StringService,
        private readonly dialogService: DialogService,
        private readonly aviAlertService: AviAlertService,
        @Inject(VirtualServiceCollection)
        VirtualServiceCollection: TVirtualServiceCollection,
        @Inject(WafPolicyCollection)
        WafPolicyCollection: TWafPolicyCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const vsCollectionConfig = {
            dataFields: [
                'config',
                'faults',
                'health',
                'runtime',
                'alert',
            ],
            bind: {
                // After new item has been created throw user to analytics page
                collItemCreate: ({ id }: VirtualServiceItem) => {
                    $state.go(VS_DETAIL_ANALYTICS, { vsId: id });
                },
            },
        };

        this.vsCollection = new VirtualServiceCollection(vsCollectionConfig);
        this.wafPolicyCollection = new WafPolicyCollection({
            params: {
                mode: WafMode.WAF_MODE_EVALUATION,
                fields: 'uuid',
            },
        });
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.vsCollection;

        this.vsGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.vsCollection,
            defaultSorting: 'name',
            fields: [],
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
                hideDelete: true,
            },
            multipleactions: [{
                label: this.l10nService.getMessage(globalL10nKeys.deleteLabel),
                id: 'delete-vs',
                disabled: (rows: IAviCollectionDataGridRow[]) => rows?.every(
                    row => !row.isDroppable(),
                ),
                onClick: (rows: IAviCollectionDataGridRow[]) => {
                    this.dialogService.add({
                        id: DELETE_VS_DIALOG_ID,
                        component: DeleteVsDialogComponent as Type<Component>,
                        componentProps: {
                            onDelete: (deleteAllReferenced = false) => {
                                this.vsCollection.dropByPriority(rows as VirtualServiceItem[], deleteAllReferenced)
                                    .then(() => {
                                        this.vsCollection.load();
                                    });
                                this.dialogService.remove(DELETE_VS_DIALOG_ID);
                            },
                            onClose: () => this.dialogService.remove(DELETE_VS_DIALOG_ID),
                        },
                    });
                },
            },
            {
                label: this.l10nService.getMessage(globalL10nKeys.enableBtnLabel),
                id: 'enable-vs',
                disabled: (rows: IAviCollectionDataGridRow[]): boolean => {
                    return !rows.some(row => this.isVsDisabled(row));
                },
                onClick: selected => {
                    this.toggleMultipleVses(selected, true);
                },
            }, {
                label: this.l10nService.getMessage(globalL10nKeys.disableBtnLabel),
                id: 'disable-vs',
                disabled: (rows: IAviCollectionDataGridRow[]): boolean => {
                    return !rows.some(row => !this.isVsDisabled(row));
                },
                onClick: selected => {
                    this.toggleMultipleVses(selected, false);
                },
            }],
        };

        if (this.authService.isPrivilegeAllowed(AviPermissionResource.PERMISSION_WAFPOLICY)) {
            this.fetchWafPoliciesInEvaluationMode();
        }
    }

    /**
     * @override
     * Template Refs must be set after the view is initialized.
     */
    public ngAfterViewInit(): void {
        this.vsGridConfig = {
            ...this.vsGridConfig,
            fields: [
                {
                    id: 'name',
                    label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                    templateRef: this.vsNameGridCellTemplate,
                    sortBy: 'name',
                    visibility: AviDataGridFieldVisibility.MANDATORY,
                }, {
                    id: 'health',
                    label: this.l10nService.getMessage(globalL10nKeys.healthLabel),
                    templateRef: this.healthTemplateRef,
                    visibility: AviDataGridFieldVisibility.MANDATORY,
                }, {
                    id: 'address',
                    label: this.l10nService.getMessage(globalL10nKeys.addressLabel),
                    templateRef: this.vsAddressGridCellTemplate,
                    visibility: AviDataGridFieldVisibility.DEFAULT,
                }, {
                    id: 'fqdn',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleAppDomainName),
                    templateRef: this.vsFqdnGridCellTemplate,
                    visibility: AviDataGridFieldVisibility.DEFAULT,
                }, {
                    id: 'services',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleServicePorts),
                    transform: (row: IAviCollectionDataGridRow): string =>
                        this.getServicesString(row.getConfig().services),
                    visibility: AviDataGridFieldVisibility.DEFAULT,
                }, {
                    id: 'pools',
                    label: this.l10nService.getMessage(l10nKeys.columnTitlePools),
                    templateRef: this.poolTemplateRef,
                    visibility: AviDataGridFieldVisibility.DEFAULT,
                }, {
                    id: 'poolgroup',
                    label: this.l10nService.getMessage(globalL10nKeys.poolGroupLabel),
                    templateRef: this.poolGroupTemplateRef,
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                }, {
                    id: 'se_group',
                    label: this.l10nService.getMessage(globalL10nKeys.serviceEngineGroupLabel),
                    transform: (row: IAviCollectionDataGridRow) => row.getConfig().se_group_ref.name(),
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                }, {
                    id: 'service_engines',
                    label: this.l10nService.getMessage(globalL10nKeys.serviceEnginesLabel),
                    templateRef: this.vsServiceEngineTemplateRef,
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                }, {
                    id: 'cloud',
                    label: this.l10nService.getMessage(globalL10nKeys.cloudLabel),
                    transform: (row: PoolGroup): string => this.stringService.name(row.getCloudRef()),
                    sortBy: 'cloud_ref',
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                }, {
                    id: 'vh_type',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleVirtualHosting),
                    templateRef: this.virtualHostingRef,
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                },
                {
                    id: 'virtual-hosting-reference',
                    label: this.l10nService.getMessage(l10nKeys.virtualHostingReferenceColumnTitle),
                    templateRef: this.virtualHostingTemplateRef,
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                },
                {
                    id: 'service_engine_count',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleTotalServiceEngines),
                    visibility: AviDataGridFieldVisibility.DEFAULT,
                    transform(vs) {
                        const runtimeData = vs.getRuntimeData();

                        if (runtimeData && Array.isArray(runtimeData.vip_summary)) {
                            return runtimeData.vip_summary.reduce((acc, vip) => {
                                if (Array.isArray(vip.service_engine)) {
                                    acc += vip.service_engine.length;
                                }

                                return acc;
                            }, 0);
                        }

                        return 0;
                    },
                }, {
                    id: 'vrf_context',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleVrfContext),
                    transform: (row: IAviCollectionDataGridRow): string => {
                        return this.stringService.name(row.getConfig().vrf_context_ref) ||
                            this.l10nService.getMessage(globalL10nKeys.emptyLabel);
                    },
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                },
            ],
            createActions: [
                {
                    label: this.l10nService.getMessage(l10nKeys.btnLabelBasicSetup),
                    onClick: () => this.vsGridConfig.collection.create(undefined, { basicSetup: true }),
                }, {
                    label: this.l10nService.getMessage(l10nKeys.btnLabelAdvancedSetup),
                    onClick: () => this.vsGridConfig.collection.create(),
                },
            ],
        };

        const gridMetricFields = [
            {
                id: 'l7_client.avg_complete_responses',
                title: 'RPS',
                label: this.l10nService.getMessage(l10nKeys.requestPerSecColumnTitle),
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.DEFAULT,
            },
            {
                id: 'l4_client.avg_complete_conns',
                title: 'CPS',
                label: this.l10nService.getMessage(l10nKeys.connectionsPerSecColumnTitle),
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.DEFAULT,
            }, {
                id: 'l4_client.max_open_conns',
                label: 'Open Conns',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.DEFAULT,
            }, {
                id: 'l4_client.avg_bandwidth',
                label: 'Throughput',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.DEFAULT,
            }, {
                id: 'l4_client.avg_total_rtt',
                label: 'Client RTT',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l4_server.avg_total_rtt',
                label: 'Server RTT',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l4_server.avg_errored_connections',
                label: 'Bad Connections',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l4_client.avg_rx_pkts',
                label: 'RX Packets',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l4_client.avg_tx_pkts',
                label: 'TX Packets',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l4_client.avg_policy_drops',
                label: 'Blocked Connections',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l4_client.avg_dos_attacks',
                label: 'DoS Attacks',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l7_client.avg_error_responses',
                label: 'Request Errors',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            }, {
                id: 'l7_client.pct_response_errors',
                label: 'Request Errors, %',
                isMetrics: true,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            },
        ];

        this.vsGridConfig.fields.push(...gridMetricFields);

        // TODO: Alerts column will not show if PERMISSION_ALERT changes from NO_ACCESS or
        // WRITE_ACCESS to READ_ACCESS unless page is reloaded.
        if (this.authService.isPrivilegeAllowed('PERMISSION_ALERT')) {
            this.vsGridConfig.fields.push({
                id: 'alerts',
                label: this.l10nService.getMessage(globalL10nKeys.alertsLabel),
                templateRef: this.alertTemplateRef,
                visibility: AviDataGridFieldVisibility.OPTIONAL,
            });
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.vsCollection.destroy();
    }

    /**
     * Navigate to virtual service alert details page for given vsId.
     */
    public redirectToVsAlertsPage(vsId: string): void {
        this.$state.go(
            VS_DETAIL_ALERTS, {
                vsId,
            },
        );
    }

    /**
     * Used to navigate to virtual service details page for given vsRef.
     */
    public navigate(vsRef: string): void {
        this.$state.go(
            VS_DETAIL_ANALYTICS,
            { vsId: this.stringService.slug(vsRef) },
        );
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public trackByFqdn(index: number, dns: { fqdn: string }): string {
        return dns.fqdn;
    }

    public redirectToVsDetailAnalytics(row: IAviCollectionDataGridRow): void {
        this.$state.go(VS_DETAIL_ANALYTICS, {
            vsId: row.id ?? '',
        });
    }

    public redirectToPoolAnalytics(row: IAviCollectionDataGridRow, poolRef: string): void {
        const poolId = this.stringService.slug(poolRef);

        this.$state.go(POOL_DETAIL_ANALYTICS, {
            vsId: row.id ?? '',
            poolId,
        });
    }

    public redirectToHealthPage(vsId: string): void {
        this.$state.go(VS_DETAIL_HS_INSIGHTS, {
            vsId,
        });
    }

    public redirectToParentVsDetailAnalytics(row: IAviCollectionDataGridRow): void {
        const vsId = this.stringService.slug(row.getConfig().vh_parent_vs_ref);

        this.$state.go(VS_DETAIL_ANALYTICS, {
            vsId: vsId ?? '',
        });
    }

    public redirectToChildVsDetailAnalytics(childRef: string): void {
        const vsId = this.stringService.slug(childRef);

        this.$state.go(VS_DETAIL_ANALYTICS, {
            vsId: vsId ?? '',
        });
    }

    public redirectToServiceEngineDetailsPage(row: IAviCollectionDataGridRow, seRef: string): void {
        const seId = this.stringService.slug(seRef);
        const cloudId = this.stringService.slug(row.getConfig().cloud_ref);

        this.$state.go(SERVICE_ENGINE_DETAIL_SUMMARY, {
            seId,
            cloudId,
        });
    }

    public getServiceEngineNames(row: IAviCollectionDataGridRow): string {
        const runtime = row.getRuntimeData();

        if (isUndefined(runtime) || !Array.isArray(runtime.vip_summary)) {
            return '';
        }

        const names = runtime.vip_summary.reduce((acc: any, vip: any) => {
            if (Array.isArray(vip.service_engine)) {
                acc.push(...vip.service_engine.map((se: any) => this.getServiceEngineLabel(se)));

                return acc;
            } else {
                return acc;
            }
        }, []);

        return names.join(', ');
    }

    /**
     * Return a string to be shown for the Service Engine, adding '(Primary)' if the SE is a
     * primary SE.
     */
    public getServiceEngineLabel(serviceengine: any): string {
        const output = serviceengine.url.name();

        return serviceengine.primary ?
            `${output} ${this.l10nService.getMessage(l10nKeys.primaryLabel)}` : output;
    }

    public getSortedServiceEngines(serviceEngines: any[]): any[] {
        return serviceEngines?.slice().sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0));
    }

    public trackByVipId(index: number, vip: any): string {
        return vip.vip_id;
    }

    public trackBySeUuid(index: number, se: any): string {
        return se.uuid;
    }

    public getVsTypeLabel(type: VirtualServiceType): string {
        switch (type) {
            case VirtualServiceType.VS_TYPE_VH_PARENT: {
                return this.l10nKeys.parentLabel;
            }

            case VirtualServiceType.VS_TYPE_VH_CHILD: {
                return this.l10nKeys.childLabel;
            }

            default:
                return '';
        }
    }

    public getVhTypeLabel(type: VHType): string {
        switch (type) {
            case VHType.VS_TYPE_VH_ENHANCED: {
                return this.l10nKeys.enhancedVHLabel;
            }

            case VHType.VS_TYPE_VH_SNI: {
                return this.l10nKeys.sniVHLabel;
            }

            default:
                return '';
        }
    }

    /**
     * Check if the WAF policy for a Virtual Service is in evaluation mode.
     */
    public isWafPolicyInEvaluationMode(row: IAviCollectionDataGridRow): boolean {
        const wafPolicyRef = row.getConfig()?.waf_policy_ref;

        if (this.wafEvaluationModePolicies?.size === 0 || !wafPolicyRef) {
            return false;
        }

        const wafPolicyUuid = this.stringService.slug(wafPolicyRef);

        return this.wafEvaluationModePolicies.has(wafPolicyUuid);
    }

    private isVsDisabled(vs: any): boolean {
        return !vs.getConfig().enabled;
    }

    private toggleMultipleVses(selectedVses: any, enableVs: boolean): any {
        const promises: any = [];

        selectedVses.forEach(function(vs: any) {
            if (vs.getConfig().enabled !== enableVs) {
                promises.push(vs.setEnabledState(enableVs));
            }
        });

        return Promise.all(promises)
            .then(() => {
                return this.vsGridConfig.collection.load();
            }).catch((rsp: any) => {
                this.aviAlertService.throw(rsp.data);
            });
    }

    /**
     * Get string of VS service to be displayed in grid.
     * @param  {Object} service - VS list of service.
     * @return {String} Comma-separated list of VS service.
     */
    private getServicesString(services: IService[] = []): string {
        return services.map((service: IService) => {
            let result = service.port.toString();

            if (service.port_range_end !== service.port) {
                result += `-${service.port_range_end}`;
            }

            if (service.enable_ssl) {
                result += ' (SSL)';
            }

            return result;
        }).join(', ');
    }

    /**
     * Fetch and store the UUIDs of WAF policies in evaluation mode.
     */
    private fetchWafPoliciesInEvaluationMode(): void {
        this.wafPolicyCollection.load()
            .then(() => {
                const wafPolicyUuids: string[] = this.wafPolicyCollection.items?.map(
                    item => item.data.config.uuid,
                ) ?? [];

                this.wafEvaluationModePolicies.clear();
                wafPolicyUuids.forEach(uuid => this.wafEvaluationModePolicies.add(uuid));
            })
            .catch(() => {
                this.wafEvaluationModePolicies.clear();
            });
    }
}
